<template>
    <v-card class="elevation-1 round-card background-1 mr-4">
        <!-- Header sempre visibile - cliccabile per toggle -->
        <v-card-title
            class="pa-3 pb-1 cursor-pointer"
            @click="$emit('toggle')"
        >
            <div class="d-flex align-center flex-wrap" style="width: 100%">
                <!-- Avatar operatore -->
                <template v-if="intervention.operator">
                    <v-avatar
                        v-if="intervention.operator.avatar"
                        size="32"
                        class="mr-2 custom-avatar"
                        :style="{'background-image': 'url(' + intervention.operator.avatar + ')'}"
                    ></v-avatar>
                    <v-avatar
                        v-else
                        :class="$eventBusUtility.bgColor(intervention.operator.id || intervention.operator_id)"
                        size="32"
                        class="mr-2"
                    >
                        <span class="white--text fs14">{{ getInitial }}</span>
                    </v-avatar>
                </template>
                <v-avatar
                    v-else
                    color="success"
                    size="32"
                    class="mr-2"
                >
                    <v-icon dark small>mdi-wrench</v-icon>
                </v-avatar>

                <div class="flex-grow-1">
                    <div class="subtitle-1 font-weight-medium" style="line-height: 1">
                        {{ intervention.operator ? intervention.operator.name : 'Intervento' }}
                    </div>
                    <div class="caption grey--text">
                        {{ interventionTypeConfig.label }} - {{ formattedDuration }}
                    </div>
                </div>
                <div class="caption grey--text text-right d-flex align-center">
                    <v-chip x-small class="mr-2" :color="interventionTypeConfig.color" dark>
                        <v-icon x-small left>{{ interventionTypeConfig.icon }}</v-icon>
                        {{ interventionTypeConfig.label }}
                    </v-chip>
                    <v-chip v-if="intervention.warranty" x-small class="mr-2" color="amber darken-2" dark>
                        <v-icon x-small left>mdi-shield-check</v-icon>
                        Garanzia
                    </v-chip>
                    <v-chip v-if="maillog" x-small class="mr-2" color="primary" dark>
                        <v-icon x-small left>mdi-email</v-icon>
                        Con Email
                    </v-chip>
                    {{ intervention.start_date | datetime }}
                    <v-icon small class="ml-2">
                        {{ collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                    </v-icon>
                </div>
            </div>
        </v-card-title>

        <!-- Preview quando collassato -->
        <v-card-text
            v-if="collapsed"
            class="pa-3 pt-0 grey--text text-truncate cursor-pointer"
            @click="$emit('toggle')"
        >
            {{ getPreview }}
        </v-card-text>

        <!-- Contenuto completo quando espanso -->
        <template v-else>
            <v-divider></v-divider>

            <!-- Dettagli intervento -->
            <v-card-text class="pa-3">
                <v-row dense>
                    <v-col cols="6" sm="3">
                        <div class="caption grey--text">Data Inizio</div>
                        <div class="body-2">{{ intervention.start_date | datetime }}</div>
                    </v-col>
                    <v-col cols="6" sm="3">
                        <div class="caption grey--text">Data Fine</div>
                        <div class="body-2">{{ intervention.end_date | datetime }}</div>
                    </v-col>
                    <v-col cols="6" sm="3">
                        <div class="caption grey--text">Durata</div>
                        <div class="body-2">{{ formattedDuration }}</div>
                    </v-col>
                    <v-col cols="6" sm="3" v-if="intervention.type_text">
                        <div class="caption grey--text">Tipo</div>
                        <div class="body-2">{{ intervention.type_text }}</div>
                    </v-col>
                </v-row>

                <!-- Descrizione intervento -->
                <div v-if="intervention.description" class="mt-3">
                    <div class="caption grey--text mb-1">Descrizione</div>
                    <div
                        v-if="isHtml(intervention.description)"
                        v-html="sanitizeHtml(intervention.description)"
                        class="intervention-body"
                    ></div>
                    <div v-else class="intervention-body">
                        {{ intervention.description }}
                    </div>
                </div>

                <!-- Note interne -->
                <div v-if="intervention.internal_notes" class="mt-3">
                    <div class="caption grey--text mb-1">
                        <v-icon x-small class="mr-1">mdi-lock</v-icon>
                        Note interne
                    </div>
                    <div
                        v-if="isHtml(intervention.internal_notes)"
                        v-html="sanitizeHtml(intervention.internal_notes)"
                        class="intervention-body grey--text text--darken-1"
                    ></div>
                    <div v-else class="intervention-body grey--text text--darken-1">
                        {{ intervention.internal_notes }}
                    </div>
                </div>
            </v-card-text>

            <!-- Email inviata (se presente) -->
            <template v-if="maillog">
                <v-divider></v-divider>
                <v-card-text class="pa-3 background-2">
                    <div class="d-flex align-center mb-2">
                        <v-icon small color="primary" class="mr-2">mdi-email-fast-outline</v-icon>
                        <span class="subtitle-2 primary--text">Email inviata al cliente</span>
                        <v-spacer></v-spacer>
                        <span v-if="maillog.send_date" class="caption grey--text">
                            {{ maillog.send_date | datetime }}
                        </span>
                    </div>

                    <!-- Destinatari -->
                    <div class="caption mb-1" v-if="maillog.to && maillog.to.length">
                        <span class="grey--text">A: </span>
                        <span>{{ formatRecipients(maillog.to) }}</span>
                    </div>
                    <div class="caption mb-2" v-if="maillog.cc && maillog.cc.length">
                        <span class="grey--text">CC: </span>
                        <span>{{ formatRecipients(maillog.cc) }}</span>
                    </div>

                    <!-- Corpo email -->
                    <div
                        v-if="isHtml(maillog.body)"
                        v-html="sanitizeHtml(maillog.body)"
                        v-inline-files
                        class="email-body pa-2 background-1 rounded"
                    ></div>
                    <div v-else class="email-body pa-2 background-1 rounded">
                        {{ maillog.body }}
                    </div>

                    <!-- Allegati email -->
                    <div v-if="maillog.attachments && maillog.attachments.length" class="mt-2">
                        <div class="caption grey--text mb-1">
                            <v-icon x-small class="mr-1">mdi-paperclip</v-icon>
                            Allegati ({{ maillog.attachments.length }})
                        </div>
                        <v-chip
                            v-for="attachment in maillog.attachments"
                            :key="attachment.id"
                            x-small
                            class="mr-1 mb-1"
                            @click="handleAttachmentClick(attachment)"
                        >
                            <v-icon left x-small>{{ getFileIcon(attachment.ext) }}</v-icon>
                            {{ attachment.origin_file_name || attachment.name }}
                        </v-chip>
                    </div>
                </v-card-text>
            </template>

            <!-- Pulsanti azioni -->
            <v-card-actions v-if="canEdit || canDelete" class="pa-2 pt-0">
                <v-spacer></v-spacer>
                <v-btn
                    v-if="canEdit"
                    icon
                    small
                    @click.stop="editIntervention"
                    title="Modifica"
                >
                    <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                    v-if="canDelete"
                    icon
                    small
                    color="error"
                    @click.stop="confirmDelete"
                    title="Elimina"
                >
                    <v-icon small>mdi-trash-can-outline</v-icon>
                </v-btn>
            </v-card-actions>
        </template>

        <file-preview-dialog
            v-model="previewDialog"
            :doc="previewDoc"
            :url="previewUrl"
        />

        <!-- Dialog conferma eliminazione -->
        <v-dialog v-model="deleteDialog" max-width="500">
            <v-card class="round-card background-2">
                <v-card-title>
                    <p>Conferma eliminazione</p>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="background-2 pa-6">
                    Vuoi eliminare questo intervento?<br>
                    <span class="caption grey--text">L'operazione non può essere annullata</span>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="deleteDialog = false">Annulla</v-btn>
                    <v-btn text color="error" @click="deleteIntervention">Elimina</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import FilePreviewDialog from '@/components/common/FilePreviewDialog'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

export default {
    name: 'InterventionTimelineItem',

    components: { FilePreviewDialog },

    props: {
        intervention: {
            type: Object,
            required: true
        },
        maillog: {
            type: Object,
            default: null
        },
        collapsed: {
            type: Boolean,
            default: false
        },
        subtype: {
            type: String,
            default: null
        },
        permissions: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            deleteDialog: false,
            previewDialog: false,
            previewDoc: null,
            previewUrl: null
        }
    },

    computed: {

        // Verifica se l'utente può modificare
        canEdit() {
            return true
        },

        // Verifica se l'utente può eliminare
        canDelete() {
            return true
        },

        // Configurazione per tipo di intervento
        interventionTypeConfig() {
            const type = this.subtype || this.intervention.type || 'intervention'
            const configs = {
                intervention: {
                    label: 'Onsite',
                    icon: 'mdi-wrench',
                    color: 'success'
                },
                intervention_remote: {
                    label: 'Remoto',
                    icon: 'mdi-remote-desktop',
                    color: 'info'
                },
                trip: {
                    label: 'Viaggio',
                    icon: 'mdi-car',
                    color: 'warning'
                }
            }
            return configs[type] || configs.intervention
        },

        getInitial() {
            if (this.intervention.operator && this.intervention.operator.name) {
                return this.intervention.operator.name.charAt(0).toUpperCase()
            }
            return 'I'
        },

        getPreview() {
            if (!this.intervention.description) return 'Nessuna descrizione'
            const text = this.intervention.description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
            return text.length > 150 ? text.substring(0, 150) + '...' : text
        },

        formattedDuration() {
            const minutes = this.intervention.duration_minutes || 0
            if (minutes === 0) return '-'
            const hours = Math.floor(minutes / 60)
            const mins = minutes % 60
            if (hours === 0) return `${mins}m`
            if (mins === 0) return `${hours}h`
            return `${hours}h ${mins}m`
        }
    },

    methods: {
        sanitizeHtml,

        isHtml(content) {
            if (!content) return false
            return /<[a-z][\s\S]*>/i.test(content)
        },

        formatRecipients(recipients) {
            if (!recipients) return ''
            if (Array.isArray(recipients)) {
                return recipients.map(r => r.email || r).join(', ')
            }
            return recipients
        },

        getFileIcon(ext) {
            const icons = {
                pdf: 'mdi-file-pdf-box',
                doc: 'mdi-file-word',
                docx: 'mdi-file-word',
                xls: 'mdi-file-excel',
                xlsx: 'mdi-file-excel',
                ppt: 'mdi-file-powerpoint',
                pptx: 'mdi-file-powerpoint',
                jpg: 'mdi-file-image',
                jpeg: 'mdi-file-image',
                png: 'mdi-file-image',
                gif: 'mdi-file-image',
                zip: 'mdi-folder-zip',
                rar: 'mdi-folder-zip',
                txt: 'mdi-file-document',
                csv: 'mdi-file-delimited'
            }
            return icons[ext?.toLowerCase()] || 'mdi-file'
        },

        isPreviewable(attachment) {
            var exts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'mp4', 'webm', 'pdf']
            return exts.includes((attachment.ext || '').toLowerCase())
        },

        handleAttachmentClick(attachment) {
            if (attachment.path && this.isPreviewable(attachment)) {
                this.previewDoc = { name: attachment.origin_file_name || attachment.name, ext: attachment.ext }
                this.previewUrl = '/files/' + attachment.path
                this.previewDialog = true
            } else {
                this.downloadAttachment(attachment)
            }
        },

        downloadAttachment(attachment) {
            if (attachment.path) {
                this.$eventBusUtility.getFile(this.$store, '/files/' + attachment.path, attachment.origin_file_name || attachment.name)
            }
        },

        // Verifica permesso dal modulo helpdesk
        hasPermission(action) {
            const modulePermissions = this.$store.getters['auth/permissions']
            if (!modulePermissions) return false
            const helpdeskPerm = modulePermissions['Helpdesk']?.['helpdeskinterventions']
            if (!helpdeskPerm) return true // Se non definito, permetti
            return helpdeskPerm === true || helpdeskPerm === action
        },

        // Apre il dialog di modifica
        editIntervention() {
            this.$emit('edit', this.intervention)
        },

        // Apre il dialog di conferma eliminazione
        confirmDelete() {
            this.deleteDialog = true
        },

        // Elimina l'intervento
        async deleteIntervention() {
            this.deleteDialog = false
            this.$store.commit('startProgress')
            await this.axios.delete(`helpdeskinterventions/${this.intervention.id}`)
            this.$store.commit('endProgress')
            this.$store.commit('setSnackbar', {
                value: true,
                text: 'Intervento eliminato',
                color: 'success',
                timeout: 3000
            })
            this.$emit('refresh')
            this.$store.commit('endProgress')
            this.$store.commit('setSnackbarError')
        }
    }
}
</script>

<style scoped>
.custom-avatar {
    background-size: cover;
    background-position: center;
}

.intervention-body {
    max-height: 300px;
    overflow-y: auto;
}

.email-body {
    max-height: 200px;
    overflow-y: auto;
    font-size: 0.875rem;
}

/* Tema scuro: la mail e' un documento del mittente e arriva con i colori scritti
   inline (Outlook forza color: rgb(0,0,0) su ogni paragrafo), che il sanitizer
   conserva per non perdere dimensioni e layout. Sul fondo scuro della card il
   testo diventava illeggibile: il body si mostra su una "carta" chiara, come
   fanno Gmail e Outlook, senza toccare i colori scelti dal mittente. */
.theme--dark .email-body {
    background-color: #fafafa;
    color: rgba(0, 0, 0, 0.87);
    padding: 12px;
    border-radius: 4px;
}

.email-body >>> img {
    max-width: 100%;
    height: auto;
}

.email-body >>> a {
    color: #1976d2;
}
</style>
